import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import "../blocks/Admin.css";
import EditConcertModal from "./EditConcertModal";
import editIcon from '../images/icons/edit-icon.svg';
import arrowIcon from '../images/icons/arrow-icon.svg';

function Admin() {
  const [editingConcert, setEditingConcert] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [concerts, setConcerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [newConcert, setNewConcert] = useState({
    city: "",
    club: "",
    address: "",
    date: "",
    time: "",
    ticketLink: "",
  });

  const handleLogin = (e) => {
    e.preventDefault();
    fetch("/api/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ login, password }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Invalid credentials");
        return res.json();
      })
      .then((data) => {
        setIsAuthenticated(true);
        setToken(data.token);
        fetchConcerts();
        setError("");
        setLogin("");
        setPassword("");
      })
      .catch((err) => {
        setError("Неверный логин или пароль");
      });
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setConcerts([]);
    setError("");
    navigate("/");
  };

  const fetchConcerts = () => {
    setLoading(true);
    fetch("/api/concerts", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch concerts");
        return res.json();
      })
      .then((data) => {
        setConcerts(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching concerts:", err);
        setError("Ошибка загрузки концертов");
        setLoading(false);
      });
  };

  const handleAddConcert = (e) => {
    e.preventDefault();
    fetch("/api/concerts", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(newConcert),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Error adding concert");
        return res.json();
      })
      .then(() => {
        fetchConcerts();
        setNewConcert({
          city: "",
          club: "",
          address: "",
          date: "",
          time: "",
          ticketLink: "",
        });
      })
      .catch((err) => {
        console.error("Error adding concert:", err);
        setError("Ошибка добавления концерта");
      });
  };

  const handleDeleteConcert = (id) => {
    if (!token) {
      setIsAuthenticated(false);
      setConcerts([]);
      setError("");
      navigate("/admin");
    }

    console.log("Deleting concert with id:", id, "Token:", token);
    fetch(`/api/concerts/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((err) => {
            throw new Error(err.error || "Failed to delete");
          });
        }
        fetchConcerts();
      })
      .catch((err) => {
        console.error("Error deleting concert:", err.message);
        setError(`Ошибка удаления: ${err.message}`);
      });
  };

  const updateConcert = (updatedData) => {
    setIsSaving(true);
    setSaveError(null);

    const { id, city, club, address, date, time, ticketLink } = updatedData;

    fetch(`/api/concerts/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ city, club, address, date, time, ticketLink }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || "Не удалось сохранить изменения");
        }
        return res.json();
      })
      .then(() => {
        setEditingConcert(null);
        fetchConcerts();
      })
      .catch((err) => {
        setSaveError(err.message);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleMoveConcert = (id, direction) => {
    fetch(`/api/concerts/${id}/reorder`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ direction }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || "Не удалось изменить порядок");
        }
        fetchConcerts();
      })
      .catch((err) => {
        console.error("Error reordering concert:", err);
        setError(err.message);
      });
  };

  const handleInputChange = (e) => {
    setNewConcert({ ...newConcert, [e.target.name]: e.target.value });
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-panel">
        <h1 className="admin-panel__title">Админ-панель</h1>
        <form className="admin-panel__form" onSubmit={handleLogin}>
          <input
            type="text"
            placeholder="Логин"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            className="admin-panel__input"
            required
          />
          <input
            type="password"
            placeholder="Пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="admin-panel__input"
            required
          />
          <button type="submit" className="admin-panel__button">
            Войти
          </button>
          {error && (
            <p className="admin-panel__error admin-panel__error--visible">
              {error}
            </p>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <h1 className="admin-panel__title">Админ-меню</h1>
      <button
        onClick={handleLogout}
        className="admin-panel__button admin-panel__button--logout"
      >
        На главную
      </button>
      <h2 className="admin-panel__subtitle">Добавить концерт</h2>
      <form className="admin-panel__form" onSubmit={handleAddConcert}>
        <input
          name="city"
          placeholder="Город (Н-р: в Москве)"
          value={newConcert.city}
          onChange={handleInputChange}
          className="admin-panel__input"
          required
        />
        <input
          name="club"
          placeholder="Место проведения (Н-р: RNDM)"
          value={newConcert.club}
          onChange={handleInputChange}
          className="admin-panel__input"
          required
        />
        <input
          name="address"
          placeholder="Адрес (Н-р: Наставнический пер., 13-15 строение 3)"
          value={newConcert.address}
          onChange={handleInputChange}
          className="admin-panel__input"
          required
        />
        <input
          name="date"
          placeholder="Дата (Н-р: 12 сентября)"
          value={newConcert.date}
          onChange={handleInputChange}
          className="admin-panel__input"
          required
        />
        <input
          name="time"
          placeholder="Время (Н-р: 20:00)"
          value={newConcert.time}
          onChange={handleInputChange}
          className="admin-panel__input"
          required
        />
        <input
          name="ticketLink"
          placeholder="Ссылка на билет (Н-р: https://exmp-link.com/)"
          value={newConcert.ticketLink}
          onChange={handleInputChange}
          className="admin-panel__input"
          required
        />
        <button type="submit" className="admin-panel__button">
          Добавить концерт
        </button>
      </form>

      <h2 className="admin-panel__subtitle">Список концертов</h2>
      {loading ? (
        <p className="admin-panel__loading">Загрузка...</p>
      ) : (
        <ul className="admin-panel__concert-list">
          {concerts.map((concert, index) => {
            const isFirst = index === 0;
            const isLast = index === concerts.length - 1;

            return (<li key={concert.id} className="admin-panel__concert-item">
              <span className="admin-panel__concert-info">
                К-рт {concert.city} - {concert.club} ({concert.date}{" "}
                {concert.time})
              </span>
              <div className="admin-panel__buttons-container">
                <button
                  onClick={() => handleDeleteConcert(concert.id)}
                  className="admin-panel__button admin-panel__button--delete"
                >
                  Удалить
                </button>
                <button
                  onClick={() => setEditingConcert(concert)}
                  className="admin-panel__button admin-panel__button--edit"
                >
                  <img className="admin-panel__edit-icon" src={editIcon} alt="Редактировать концерт" />
                </button>
                <div className="admin-panel__arrows-container">
                  <button
                    onClick={() => handleMoveConcert(concert.id, 'up')}
                    className={`admin-panel__button admin-panel__button--arrow ${isFirst ? 'disabled-button' : ''}`}
                    disabled={isFirst}
                  >
                    <img className="admin-panel__move-icon even-icon" src={arrowIcon} alt="Вверх" />
                  </button>
                  <button
                    onClick={() => handleMoveConcert(concert.id, 'down')}
                    className={`admin-panel__button admin-panel__button--arrow ${isLast ? 'disabled-button' : ''}`}
                    disabled={isLast}
                  >
                    <img className="admin-panel__move-icon" src={arrowIcon} alt="Вниз" />
                  </button>
                </div>
              </div>
            </li>)
          })}
        </ul>
      )}
      {error && (
        <p className="admin-panel__error admin-panel__error--visible">
          {error}
        </p>
      )}
      {editingConcert && (
        <EditConcertModal
          concert={editingConcert}
          onClose={() => setEditingConcert(null)}
          onSave={updateConcert}
          isLoading={isSaving}
          error={saveError}
          onCloseError={() => setSaveError(null)}
        />
      )}
    </div>
  );
}

export default Admin;
